import React, { useState } from 'react';
import classnames from 'classnames';
import Select from 'react-select';
import { MenuPosition } from 'react-select/lib/types';

import { ArrowIcon, ChevronIcon } from '../icons/icons';
import { Icon } from '../icon';
import Colors from '../colors';

import styles from './dropdown.css';

interface Option<ValueType> {
  readonly value: ValueType;
  readonly label: string;
}

export interface Props<ValueType> {
  /** List of selectable options */
  readonly options: readonly Option<ValueType>[];
  /** Callback when selection is changed. If not provided, the dropdown will be disabled. */
  readonly onChange?: (value: ValueType) => void;
  /** CSS width of dropdown */
  readonly width?: string;
  /** Minimal UI dropdown */
  readonly minimal?: boolean;
  /** Current selected value. `ValueType` can be any type, but most typically string or number. */
  readonly value: ValueType;
  /** Adds additional classes to the component. */
  readonly className?: string;
  /** Sets ID for the input element */
  readonly inputId?: string;
  /** Provides a data-testid */
  'data-testid'?: string;
  /** Disables the Dropdown, if set to true */
  isDisable?: boolean;
  menuShouldBlockScroll?: boolean;
  menuPosition?: MenuPosition;
  menuPortalTarget?: HTMLElement;
}

export const Dropdown = <ValueType extends {}>({
  className,
  onChange,
  options,
  'data-testid': dataTestId,
  value,
  inputId,
  width = '150px',
  minimal = false,
  isDisable = false,
  menuShouldBlockScroll = false,
  menuPosition = 'fixed',
  menuPortalTarget = document.body
}: Props<ValueType>) => {
  // react-select does not add a CSS class when the dropdown is open, so we use this state
  // property to add one ourselves.
  const [isOpen, setIsOpen] = useState(false);
  const isDisabled = !onChange || isDisable;

  const handleChange = (option: Option<ValueType> | Option<ValueType>[] | undefined | null) => {
    /* istanbul ignore next */
    if (onChange && option && !Array.isArray(option)) {
      onChange(option.value);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const getValue = () => {
    const selectedOption = options.find(option => option.value === value);

    /* istanbul ignore next */
    return {
      value,
      label: selectedOption ? selectedOption.label : ''
    };
  };

  return (
    <div style={{ width }} data-testid={dataTestId}>
      <Select
        className={classnames(styles.dropdown, className, {
          [styles.minimal]: minimal,
          [styles.isOpen]: isOpen
        })}
        classNamePrefix="select"
        // z-index 203 to override the modal overlay z-index 202
        styles={{ menuPortal: defaultStyles => ({ ...defaultStyles, zIndex: 203 }) }}
        isClearable={false}
        isDisabled={isDisabled}
        isSearchable={true}
        // @ts-ignore
        onChange={handleChange}
        onMenuClose={handleClose}
        onMenuOpen={handleOpen}
        menuShouldBlockScroll={menuShouldBlockScroll}
        menuPosition={menuPosition}
        menuPortalTarget={menuPortalTarget}
        options={options}
        components={{
          DropdownIndicator: () => (
            <Icon
              color={isDisabled ? Colors.SilverAlto : isOpen ? Colors.UnbounceBlue : Colors.Emperor}
              size={minimal ? '10px' : '18px'}
              className={classnames(styles.arrowIcon, {
                [styles.rotateIcon]: minimal
              })}
            >
              {minimal ? <ChevronIcon /> : <ArrowIcon />}
            </Icon>
          )
        }}
        instanceId={'dropdown'}
        inputId={inputId}
        value={getValue()}
      />
    </div>
  );
};

export default Dropdown;
